import React, { createContext, useContext, useMemo } from "react";
import {
  useNetworkStatus,
  NetworkStatus,
  UseNetworkStatusOptions,
} from "@/hooks";

export interface NetworkContextValue extends NetworkStatus {
  /** Whether the device is currently offline (combines isOnline and isConnected) */
  isOffline: boolean;

  /** Check connectivity manually */
  checkConnectivity: () => Promise<void>;

  /** Refresh network status */
  refresh: () => void;

  /** Get a human-readable connection status */
  getConnectionStatusText: () => string;

  testUrl?: string;
}

const NetworkContext = createContext<NetworkContextValue | undefined>(
  undefined
);

export interface NetworkProviderProps {
  children: React.ReactNode;

  /** Options for the network status hook */
  options?: UseNetworkStatusOptions;
}

export const NetworkProvider: React.FC<NetworkProviderProps> = ({
  children,
  options = {},
}) => {
  const {
    checkInterval = 30000, // Check every 30 seconds
    testUrl = import.meta.env.VITE_GATEWAY_ENDPOINT,
    testTimeout = 5000,
    enableConnectivityTest = true,
  } = options;

  // Use your existing network status hook and destructure the methods
  const networkStatusHook = useNetworkStatus({
    checkInterval,
    testUrl,
    testTimeout,
    enableConnectivityTest,
  });

  // Compute derived values
  const contextValue: NetworkContextValue = useMemo(() => {
    const isOffline =
      !networkStatusHook.isOnline || !networkStatusHook.isConnected;

    const getConnectionStatusText = (): string => {
      if (!networkStatusHook.isOnline) {
        return "Device is offline";
      }

      if (!networkStatusHook.isConnected) {
        return "No server connection";
      }

      if (networkStatusHook.connectionType) {
        return `Connected (${networkStatusHook.connectionType})`;
      }

      return "Connected";
    };

    return {
      ...networkStatusHook,
      isOffline,
      testUrl,
      getConnectionStatusText,
    };
  }, [networkStatusHook]);

  return (
    <NetworkContext.Provider value={contextValue}>
      {children}
    </NetworkContext.Provider>
  );
};

// Custom hook for using the network context
export const useNetwork = (): NetworkContextValue => {
  const context = useContext(NetworkContext);
  if (context === undefined) {
    throw new Error("useNetwork must be used within a NetworkProvider");
  }
  return context;
};

// Hook for getting just the offline status (most common use case)
export const useIsOffline = (): boolean => {
  const { isOffline } = useNetwork();
  return isOffline;
};

// Hook for getting just the online status
export const useIsOnline = (): boolean => {
  const { isOnline, isConnected } = useNetwork();
  return isOnline && isConnected;
};
